import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import config from "../config";
import logo from "../logo.svg";
import logoDefault from "../logo-default.svg";
import logoAristas from "../logo-aristas.svg";
import logoPeluca from "../logo-peluca.jpg";

// Datos simulados para demo
const mockUsers = [
  {
    id: "doctor-1",
    email: "doctor@hospital.com",
    name: "Dr. Juan Pérez",
    role: "doctor",
    createdAt: new Date(),
    specialization: "Cardiología",
    privateAppointmentPrice: 5000,
  },
  {
    id: "secretary-1",
    email: "secretary@hospital.com",
    name: "María González",
    role: "secretary",
    createdAt: new Date(),
    hospitalName: "Hospital Central",
  },
  {
    id: "patient-1",
    email: "patient@email.com",
    name: "Carlos Rodríguez",
    role: "patient",
    createdAt: new Date(),
    phone: "+54 11 1234-5678",
  },
];

export const featureFlags = [];

export const getFeatureFlagValue = (feature) => {
  return featureFlags.filter((a) => a.feature === feature)[0].isEnabled;
};

export const tw = String.raw;

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const uploadResource = async (data) => {
  const formData = new FormData();
  formData.append("file", data.file[0]);

  const res = await fetch(config.resourcesLink, {
    method: "POST",
    body: formData,
  }).then((res) => res.json());

  return res;
};

export const getInviteLink = (user_id) => {
  return config.inviteLink + user_id;
};

export const delay = (ms) => new Promise((res) => setTimeout(res, ms));

export const getStartOfWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // Lunes

  d.setDate(d.getDate() + 1);
  d.setHours(0, 0, 0, 0);

  return d;
};

export const getWeekDays = (start) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const days = [...Array(7)].map((_, i) => {
    const day = new Date(start);
    day.setDate(day.getDate() + i);
    day.setHours(0, 0, 0, 0);
    return day;
  });

  // Si la semana es la actual, filtramos los días anteriores a hoy
  if (start <= today && today <= addDays(start, 6)) {
    return days.filter((day) => day >= today);
  }

  return days;
};

export const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

export const getMercadoPagoAccessToken = async (code) => {
  try {
    const response = await fetch(`${config.baseUrlApi}/mercadopago/token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`Error al obtener token desde backend: ${errorData}`);
    }

    return await response.json();
  } catch (error) {
    return { error: error.message };
  }
};

export const getBrandLogo = () => {
  return config.theme.logo;
};

export const isBinapsisBrand = () =>
  (config.brand || "").toUpperCase() === "BINAPSIS";

export const getBrandLogoClassName = () =>
  isBinapsisBrand() ? "h-10 w-auto object-contain" : "w-12 h-12 object-cover";

export const getBrandLogoSrc = () =>
  `${process.env.PUBLIC_URL || ""}${getBrandLogo()}`;

export const getBrandAnimatedLoaderSrc = () => {
  const loaderPath = config.theme?.animatedLoaderLogo || config.theme?.logo;
  return `${process.env.PUBLIC_URL || ""}${loaderPath}`;
};

export const switchUser = (userId) => {
  const foundUser = mockUsers.find((u) => u.id === userId);
  if (foundUser) {
    setUser(foundUser);
    localStorage.setItem("currentUserId", foundUser.id);
  }
};

export const goBack = () => {
  return history.go(-1);
};

/** Profesional habilitado por administración (null/undefined cuenta como habilitado). */
export const isProfessionalEnabled = (profile) => {
  if (!profile) return false;
  if (profile.role !== "doctor") return true;
  return profile.is_professional_enabled !== false;
};

/** Médico visible y apto para recibir turnos. */
export const isBookableDoctor = (profile) =>
  profile?.role === "doctor" && isProfessionalEnabled(profile);

/**
 * ¿Este usuario puede actuar como paciente? (buscar profesionales, reservar
 * turnos para sí o para sus familiares, ver sus turnos como paciente).
 * Los pacientes siempre; los profesionales también, reusando su propio perfil
 * como patient_id. Ver CLAUDE.md → "Profesional como paciente".
 */
export const canActAsPatient = (user) =>
  user?.role === "patient" || user?.role === "doctor";

/** Nombre público de institución o asociación. */
export const getOrganizationDisplayName = (profile) => {
  if (!profile) return "";
  const fallback =
    profile.role === "association" ? "Asociación" : "Institución";
  return (
    profile.fantasy_name?.trim() ||
    profile.hospital_name?.trim() ||
    `${profile.name || ""} ${profile.last_name || ""}`.trim() ||
    fallback
  );
};

/**
 * Institución (perfil) con la que el profesional comparte agenda, o null.
 * Toma el primer horario compartido con un perfil de rol "institution".
 */
export const findDoctorInstitution = (
  doctorId,
  { sharedSchedules, secretaries, getSchedulesByDoctor }
) => {
  if (!doctorId || !sharedSchedules || !secretaries || !getSchedulesByDoctor) {
    return null;
  }
  const scheduleIds = (getSchedulesByDoctor(doctorId) || []).map((s) => s.id);
  if (scheduleIds.length === 0) return null;
  for (const shared of sharedSchedules) {
    if (!scheduleIds.includes(shared.schedule_id)) continue;
    const secretary = secretaries.find((s) => s.id === shared.secretary_id);
    if (secretary?.role === "institution") return secretary;
  }
  return null;
};

/** Nombre para listados de secretarias / instituciones compartidas. */
export const getStaffDisplayName = (profile) => {
  if (!profile) return "Secretaria";
  if (profile.role === "institution" || profile.role === "association") {
    return getOrganizationDisplayName(profile);
  }
  return `${profile.name || ""} ${profile.last_name || ""}`.trim() || "Secretaria";
};

/** Subtítulo opcional bajo el nombre en selects de staff. */
export const getStaffDisplaySubtitle = (profile) => {
  if (!profile) return "";
  if (profile.role === "institution") {
    return profile.institution_type || profile.email || "";
  }
  if (profile.role === "association") {
    return profile.email || "";
  }
  return profile.hospital_name || "";
};

export const isLocal = () => {
  return window.location.hostname.includes("localhost");
};

export const isProduction = () => {
  return window.location.hostname.includes("aristas-adc75de38188.herokuapp.com");
};

export const isDevelopment = () => {
  return window.location.hostname.includes("aristas-adc75de38188.herokuapp.com");
};